import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { getPortfolioReportingSummaryQuery } from "web_myplann/Api/Portfolio/Queries/getPortfolioReportingSummaryQuery";
import { usePortfolioListQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioListQuery";
import { usePropertyListQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyListQuery";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { PORTFOLIO_SORT } from "web_myplann/Portfolio/Components/Redesign/Filter/portfolioSortKey";
import { flattenAndSortCategories } from "web_myplann/Portfolio/Helpers/portfolioCategorySort";
import { getReportingPortfolioIds } from "web_myplann/Portfolio/Helpers/portfolioReportingDomains";
import { usePortfolioCategories } from "web_myplann/Portfolio/Hooks/usePortfolioCategories";
import {
	type PortfolioListView,
	PortfolioViewMode,
} from "web_myplann/Portfolio/Types/portfolioCategory";
import { portfolioFilterState } from "web_myplann/Shared/State/portfolioFilterAtom";

type PortfolioListData = {
	view: PortfolioListView;
	totalItems: number;
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
	isLoading: boolean;
	hasNoData: boolean;
};

export const usePortfolioListData = (): PortfolioListData => {
	const filter = useAtomValue(portfolioFilterState);

	const {
		data: portfolioList,
		isLoading: isPortfolioLoading,
		isSuccess: isPortfolioSuccess,
	} = usePortfolioListQuery();
	const {
		data: propertyList,
		isLoading: isPropertyLoading,
		isSuccess: isPropertySuccess,
	} = usePropertyListQuery();

	const portfolioItems = useMemo(
		() => portfolioList?.data ?? [],
		[portfolioList?.data],
	);
	const propertyItems = useMemo(
		() => propertyList?.data ?? [],
		[propertyList?.data],
	);

	const reportingIds = useMemo(
		() => getReportingPortfolioIds(portfolioItems),
		[portfolioItems],
	);
	const { data: reportingSummary } =
		getPortfolioReportingSummaryQuery(reportingIds);
	const summaryData = reportingSummary?.data;

	const selectedCategories = useMemo(
		() =>
			filter.categories.length > 0 ? new Set(filter.categories) : undefined,
		[filter.categories],
	);

	const isGrouped = filter.sort === PORTFOLIO_SORT.oblast;

	const categories = usePortfolioCategories(portfolioItems, propertyItems, {
		categories: selectedCategories,
		sort: filter.sort,
		sortDesc: isGrouped ? false : filter.sortDesc,
		summaryData,
	});

	const groupedCategories = useMemo(
		() => (filter.sortDesc ? [...categories].reverse() : categories),
		[categories, filter.sortDesc],
	);

	const view = useMemo<PortfolioListView>(
		() =>
			isGrouped
				? { mode: PortfolioViewMode.grouped, categories: groupedCategories }
				: {
						mode: PortfolioViewMode.flat,
						flatItems: flattenAndSortCategories(
							categories,
							filter.sort,
							filter.sortDesc,
							summaryData,
						),
					},
		[
			isGrouped,
			categories,
			groupedCategories,
			filter.sort,
			filter.sortDesc,
			summaryData,
		],
	);

	const totalItems = useMemo(
		() => categories.reduce((sum, category) => sum + category.items.length, 0),
		[categories],
	);

	const isLoading = isPortfolioLoading || isPropertyLoading;

	const hasNoData =
		isPortfolioSuccess &&
		isPropertySuccess &&
		portfolioItems.length === 0 &&
		propertyItems.length === 0;

	return {
		view,
		totalItems,
		summaryData,
		isLoading,
		hasNoData,
	};
};
